import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Toolbar,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SchoolIcon from "@mui/icons-material/School";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useEffect, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import type { AcademicYear, Term } from "../api/types";
import { loadLibreBaskerville } from "../theme/headingFont";
import ChildrenSettingsDialog from "./ChildrenSettingsDialog";

const nav = [
  { to: "/teach", label: "Teach" },
  { to: "/planning", label: "Lesson Plans" },
  { to: "/timetable", label: "Timetable" },
  { to: "/calendar", label: "Calendar" },
];

const CHILD_EDIT_VALUE = "__edit__";

function resolveActiveNav(pathname: string) {
  // Lesson plan editor is reached from Lesson Plans (and Teach), but isn't its own nav item.
  if (pathname.startsWith("/scheme")) {
    return nav.find((item) => item.to === "/planning") ?? nav[0];
  }
  return nav.find((item) => pathname.startsWith(item.to)) ?? nav[0];
}

function termLabel(term: Term, year: AcademicYear): string {
  const name = term.name.trim() || "Untitled term";
  return `${name} ${year.name}`;
}

export default function AppLayout() {
  const { user, child, children, year, years, term, logout, setTermId, setChildId } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    loadLibreBaskerville();
  }, []);

  const activeNav = resolveActiveNav(location.pathname);

  const yearsWithTerms = years.filter((y) => y.terms.length > 0);
  const selectedYear =
    (term && years.find((y) => y.terms.some((t) => t.id === term.id))) || year;
  const termText =
    term && selectedYear
      ? termLabel(term, selectedYear)
      : term?.name.trim() || null;

  function onTermChange(event: SelectChangeEvent<string>) {
    const nextId = Number(event.target.value);
    if (!Number.isFinite(nextId)) return;
    void setTermId(nextId);
  }

  function onChildChange(event: SelectChangeEvent<string>) {
    const value = event.target.value;
    if (value === CHILD_EDIT_VALUE) {
      setSettingsOpen(true);
      return;
    }
    const nextId = Number(value);
    if (!Number.isFinite(nextId) || nextId === child?.id) return;
    void setChildId(nextId);
  }

  function closeProfileMenu() {
    setProfileAnchor(null);
  }

  const selectSx = {
    color: "inherit",
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.55)",
    },
  } as const;

  const labelSx = {
    color: "rgba(255,255,255,0.9)",
    "&.Mui-focused": { color: "rgba(255,255,255,0.9)" },
    "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.9)" },
    "&.MuiInputLabel-shrink": { color: "rgba(255,255,255,0.9)" },
  } as const;

  const selectFieldSx = {
    ...selectSx,
    width: "100%",
    maxWidth: "100%",
    bgcolor: { xs: "rgba(255,255,255,0.2)", md: "rgba(255,255,255,0.12)" },
    borderRadius: 1,
    "& .MuiSelect-select": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  } as const;

  const dropdownControlSx = {
    minWidth: 0,
    flexShrink: 1,
    // Space for the notched label; keep bg on the Select so it doesn't spill above the outline
    mt: 0.5,
  } as const;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50", overflowX: "hidden", width: "100%" }}>
      <AppBar
        position="static"
        elevation={0}
        color="primary"
        sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}
      >
        <Toolbar
          disableGutters
          sx={{
            gap: { xs: 1.5, md: 2 },
            flexWrap: { md: "wrap" },
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            py: { xs: 1.5, md: 1 },
            px: { xs: 1.5, sm: 3 },
            minHeight: { xs: "auto", md: 64 },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              maxWidth: "100%",
              flexGrow: { md: 1 },
            }}
          >
            School Happy
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {nav.map((item) => (
              <Button
                key={item.to}
                color="inherit"
                component={RouterLink}
                to={item.to}
                data-onboarding-id={
                  item.to === "/teach"
                    ? "nav-teach"
                    : item.to === "/timetable"
                      ? "nav-timetable"
                      : item.to === "/planning"
                        ? "nav-planning"
                        : undefined
                }
                variant={activeNav.to === item.to ? "outlined" : "text"}
                sx={{ px: 1 }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              maxWidth: "100%",
              justifyContent: { xs: "flex-end", md: "flex-start" },
            }}
          >
            <FormControl
              size="small"
              sx={{
                ...dropdownControlSx,
                width: { xs: "auto", md: 280 },
                minWidth: 0,
                flex: { xs: "1 1 0", md: "0 0 auto" },
                maxWidth: { xs: "45%", md: 280 },
              }}
            >
              <InputLabel id="term-label" shrink sx={labelSx}>
                Term
              </InputLabel>
              <Select
                labelId="term-label"
                label="Term"
                fullWidth
                value={term ? String(term.id) : ""}
                displayEmpty
                notched
                disabled={yearsWithTerms.length === 0}
                onChange={onTermChange}
                renderValue={() =>
                  term
                    ? (termText ?? term.name.trim()) || "Untitled term"
                    : "No terms"
                }
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 360 } },
                }}
                sx={selectFieldSx}
              >
                {yearsWithTerms.flatMap((y) => [
                  <ListSubheader key={`year-${y.id}`} disableSticky sx={{ lineHeight: 2.2 }}>
                    {y.name}
                    {y.is_active ? " (current)" : ""}
                  </ListSubheader>,
                  ...[...y.terms]
                    .sort((a, b) => a.order_index - b.order_index || a.id - b.id)
                    .map((t) => (
                      <MenuItem key={t.id} value={String(t.id)} sx={{ pl: 3 }}>
                        {t.name.trim() || "Untitled term"}
                      </MenuItem>
                    )),
                ])}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{
                ...dropdownControlSx,
                width: { xs: "auto", md: 160 },
                minWidth: 0,
                flex: { xs: "1 1 0", md: "0 0 auto" },
                maxWidth: { xs: "40%", md: 200 },
              }}
            >
              <InputLabel id="child-label" shrink sx={labelSx}>
                Child
              </InputLabel>
              <Select
                labelId="child-label"
                label="Child"
                fullWidth
                value={child ? String(child.id) : ""}
                displayEmpty
                notched
                onChange={onChildChange}
                renderValue={() => child?.name.trim() || "No children"}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 360 } },
                }}
                sx={selectFieldSx}
              >
                <MenuItem value={CHILD_EDIT_VALUE}>Edit...</MenuItem>
                <Divider component="li" sx={{ my: 0.5 }} />
                {children.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name.trim() || "Untitled"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              color="inherit"
              aria-label="Account menu"
              aria-controls={profileAnchor ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={profileAnchor ? "true" : undefined}
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              sx={{
                flexShrink: 0,
                bgcolor: "rgba(255,255,255,0.14)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
              }}
            >
              <SchoolIcon />
            </IconButton>
          </Box>
          <Menu
            id="account-menu"
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfileMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: { sx: { minWidth: 220, mt: 1 } },
            }}
          >
            <MenuItem disabled sx={{ opacity: "1 !important", cursor: "default" }}>
              <ListItemText
                primary={user?.email ?? "Signed in"}
                secondary={child?.name ? `Planning for ${child.name}` : undefined}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                closeProfileMenu();
                setSettingsOpen(true);
              }}
            >
              <ListItemIcon>
                <SettingsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeProfileMenu();
                logout();
                navigate("/login");
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Log out</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3, minWidth: 0 }}>
        <Outlet />
      </Container>
      <ChildrenSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
}
